import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Tags, History, PlayCircle, PauseCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/* Central pricing management: every product's selling price is controlled
   here as versioned price records — create a new price, suspend it, or
   re-activate an earlier one. Portal orders always use the active record. */
export default function Pricing() {
  const [overview, setOverview] = useState([]);
  const [managing, setManaging] = useState(null); // product row being managed

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pricing/overview`, authHeaders());
      setOverview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load pricing overview');
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const managed = overview.filter((r) => r.active_price).length;

  return (
    <div data-testid="pricing-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Pricing Management</h1>
        <p className="text-slate-600 mt-1">
          All product prices are controlled from here — create a new price, suspend it, or re-activate a previous one.
          Customer orders always use the price active at the time of ordering.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 max-w-2xl">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-gray-500">Products</p>
          <p className="text-2xl font-bold">{overview.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-gray-500">With managed price</p>
          <p className="text-2xl font-bold text-green-600">{managed}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-gray-500">On default tariff</p>
          <p className="text-2xl font-bold text-amber-600">{overview.length - managed}</p>
        </CardContent></Card>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Current Retail</TableHead>
              <TableHead>Wholesale Tiers</TableHead>
              <TableHead>Pricing Status</TableHead>
              <TableHead>Records</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overview.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500">
                No products in the catalog yet.
              </TableCell></TableRow>
            ) : overview.map((row) => (
              <TableRow key={row.product_id} data-testid={`pricing-row-${row.product_id}`}>
                <TableCell className="font-medium">{row.product_name}</TableCell>
                <TableCell className="capitalize">{row.category}</TableCell>
                <TableCell>
                  Tsh {((row.active_price?.retail_price ?? row.fallback_retail) || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-xs">
                  {(row.active_price?.wholesale_tiers || []).map((t) => `${t.min_qty}+ → ${t.price.toLocaleString()}`).join(' · ') || '—'}
                </TableCell>
                <TableCell>
                  {row.active_price ? (
                    <Badge className="bg-green-100 text-green-800">Managed</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">Default tariff</Badge>
                  )}
                </TableCell>
                <TableCell>{row.price_records}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setManaging(row)} data-testid={`manage-pricing-${row.product_id}`}>
                    <Tags size={14} className="mr-1" /> Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {managing && (
        <ManagePricesDialog row={managing} onClose={() => { setManaging(null); fetchOverview(); }} />
      )}
    </div>
  );
}

function ManagePricesDialog({ row, onClose }) {
  const [prices, setPrices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ retail_price: row.active_price?.retail_price || row.fallback_retail || 0, notes: '',
    // A new price replaces the one in force, so it starts from its MOQ tiers —
    // otherwise every change silently dropped the bands staff had entered.
    wholesale_tiers: (row.active_price?.wholesale_tiers || []).map((t) => ({ ...t })) });

  const fetchPrices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products/${row.product_id}/prices`, authHeaders());
      setPrices(res.data);
    } catch {
      toast.error('Failed to load price history');
    }
  }, [row.product_id]);

  useEffect(() => { fetchPrices(); }, [fetchPrices]);

  const addTier = () => setForm((f) => ({ ...f, wholesale_tiers: [...f.wholesale_tiers, { min_qty: 0, price: 0 }] }));
  const setTier = (i, key, value) => setForm((f) => {
    const tiers = [...f.wholesale_tiers];
    tiers[i] = { ...tiers[i], [key]: Number(value) || 0 };
    return { ...f, wholesale_tiers: tiers };
  });
  const removeTier = (i) => setForm((f) => ({ ...f, wholesale_tiers: f.wholesale_tiers.filter((_, j) => j !== i) }));

  const createPrice = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/products/${row.product_id}/prices`, form, authHeaders());
      toast.success('New price activated — previous price suspended');
      setShowForm(false);
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create price');
    } finally {
      setSaving(false);
    }
  };

  const suspend = async (id) => {
    if (!window.confirm('Suspend this price? The product falls back to the default tariff until a price is activated.')) return;
    try {
      await axios.put(`${API}/product-prices/${id}/disable`, {}, authHeaders());
      toast.success('Price suspended');
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend price');
    }
  };

  const toggleSelected = (id) => setSelected((ids) =>
    ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  // Pricing by MOQ leaves a record per band. Activating them together merges
  // their bands into the one price list the system reads, so every MOQ is in
  // force at once instead of the last one activated winning.
  const activateSelected = async () => {
    try {
      await axios.post(`${API}/products/${row.product_id}/prices/activate-many`,
        { price_ids: selected }, authHeaders());
      toast.success(selected.length > 1
        ? `${selected.length} prices merged into one active price list`
        : 'Price activated');
      setSelected([]);
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate prices');
    }
  };

  const activate = async (id) => {
    try {
      await axios.put(`${API}/product-prices/${id}/activate`, {}, authHeaders());
      toast.success('Price activated');
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate price');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="manage-prices-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} /> Price History — {row.product_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-2">
            <p className="text-sm text-gray-500">
              Tick the prices that should be in force together — their MOQ bands merge into one active
              price list. History is never deleted.
            </p>
            <div className="flex gap-2 shrink-0">
              {selected.length > 0 && (
                <Button size="sm" variant="outline" className="text-green-700" onClick={activateSelected}
                  data-testid="activate-selected-prices">
                  <PlayCircle size={14} className="mr-1" /> Activate {selected.length} selected
                </Button>
              )}
              <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="pricing-new-price-button">
                <Plus size={14} className="mr-1" /> New Price
              </Button>
            </div>
          </div>

          {showForm && (
            <form onSubmit={createPrice} className="border rounded-md p-4 space-y-3 bg-slate-50">
              <div className="space-y-1">
                <Label>Retail Price (Tsh) *</Label>
                <Input type="number" min="1" required value={form.retail_price} data-testid="pricing-retail-input"
                  onChange={(e) => setForm({ ...form, retail_price: Number(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label>Wholesale Tiers (minimum quantity → unit price)</Label>
                {form.wholesale_tiers.map((tier, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input type="number" min="1" placeholder="Min qty" value={tier.min_qty || ''}
                      onChange={(e) => setTier(i, 'min_qty', e.target.value)} required />
                    <span className="text-gray-400">→ Tsh</span>
                    <Input type="number" min="1" placeholder="Unit price" value={tier.price || ''}
                      onChange={(e) => setTier(i, 'price', e.target.value)} required />
                    <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => removeTier(i)}>×</Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={addTier}>+ Add tier</Button>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={form.notes} placeholder="e.g. Aug 2026 price review"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="pricing-save-price-button">
                {saving ? 'Saving...' : 'Activate New Price'}
              </Button>
            </form>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Retail</TableHead>
                <TableHead>Wholesale Tiers</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prices.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-400">
                  No price records — this product uses the built-in default tariff.
                </TableCell></TableRow>
              )}
              {prices.map((rec) => (
                <TableRow key={rec.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={selected.includes(rec.id)}
                      onChange={() => toggleSelected(rec.id)}
                      data-testid={`select-price-${rec.id}`}
                      aria-label={`Select price from ${(rec.created_at || '').slice(0, 10)}`}
                    />
                  </TableCell>
                  <TableCell className="text-xs">{(rec.created_at || '').slice(0, 10)}<br />
                    <span className="text-gray-400">{rec.created_by}</span></TableCell>
                  <TableCell>Tsh {(rec.retail_price || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">
                    {(rec.wholesale_tiers || []).map((t) => `${t.min_qty}+ → ${t.price.toLocaleString()}`).join(' · ') || '—'}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{rec.notes || ''}</TableCell>
                  <TableCell>
                    <Badge className={rec.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {rec.status === 'active' ? 'active' : 'suspended'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {rec.status === 'active' ? (
                      <Button size="sm" variant="outline" className="text-amber-700" onClick={() => suspend(rec.id)}
                        data-testid={`suspend-price-${rec.id}`}>
                        <PauseCircle size={14} className="mr-1" /> Suspend
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="text-green-700" onClick={() => activate(rec.id)}
                        data-testid={`activate-price-${rec.id}`}>
                        <PlayCircle size={14} className="mr-1" /> Activate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
